import { List, Map } from "immutable";
import forEach from "lodash/forEach";
import orderBy from "lodash/orderBy";
import moment from "moment";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import Task from "~/records/record.task.jsx";
import { getCompletedTaskListsByProjectId } from "~/store/selectors/selector.taskLists";
import { filterByDate } from "~/utils/date";
import { sortCompare, descSortCompare } from "~/utils/utils.jsx";
import {
  getConfigOption,
  getFilterCalendarConfigOption,
  getIsMultipleAssigneesEnabled,
} from "./selector.configOptions.jsx";
import {
  makeGetProjectFiltersByTaskListIdSelector,
  makeSelectedLabelIdFilterSelector,
  makeSelectedAssigneeIdFilterSelector,
  makeSelectedDateIdFilterSelector,
  getProjectFiltersById,
} from "./selector.configOptions.jsx";
import { getAllLabels } from "./selector.labels.jsx";

export const getTaskById = (state, id) => state.tasks.get(`${id}`);
export const getTaskByIdObject = createSelector(getTaskById, (task) => task?.toObject());
export const getAllTasks = (state) => state.tasks;
export const getAllTasksByProjectId = createSelector(
  getAllTasks,
  (state, projectId) => projectId,
  (tasks, projectId) => tasks.filter((t) => t && !t.is_trashed && t.project_id === projectId)
);
export const getProjectIdByTaskId = createSelector(getTaskById, (task) => (task ? task.project_id : 0));
export const getIsTaskHiddenFromClients = (state, taskId) => getTaskById(state, taskId).is_hidden_from_clients;
export const getDefaultTaskById = (state, id) => state.tasks.get(`${id}`, new Task());

export const getOpenTasks = createSelector(getAllTasks, (tasks) =>
  tasks.filter((t) => !t.is_trashed && !t.is_completed)
);

export const getCompletedTasks = createSelector(getAllTasks, (tasks) =>
  tasks.filter((t) => !t.is_trashed && t.is_completed)
);

export const getOpenAndCompletedTasks = createSelector(getAllTasks, (tasks) => tasks.filter((t) => !t.is_trashed));

export const getMapOfActiveTaskIdsByTaskListId = createSelector(getAllTasks, (tasks) => {
  return tasks.reduce((acc, task) => {
    if (typeof task?.get("task_list_id") === "number") {
      if (typeof acc[`${task.get("task_list_id")}`] === "undefined") {
        acc[`${task.get("task_list_id")}`] = [];
      }

      if (
        !task?.get("is_trashed") &&
        // check if task id is string is for loading task (id is uuid)
        (typeof task?.get("id") === "number" || typeof task?.get("id") === "string")
      ) {
        acc[`${task.get("task_list_id")}`].push(task.get("id"));
      }
    }

    return acc;
  }, {});
});

export const getTasksByIds = createSelector(
  getAllTasks,
  (state, taskIds) => taskIds,
  (tasks, taskIds) => {
    return taskIds.map((taskId) => tasks.get(`${taskId}`)).filter(Boolean);
  }
);

export const getSortedOpenAndCompletedTasks = createSelector(
  getOpenAndCompletedTasks,
  (state, sort = "name") => sort,
  (tasks, sort) =>
    tasks
      .sortBy((task) => {
        const value = task.get(`${sort}`, task.get("name"));
        return typeof value === "string" ? value.toLowerCase() : value;
      })
      .toArray()
);

const defaultOpenDependencies = Map();
export const getOpenDependenciesById = (state, id) => {
  return getTaskById(state, id)?.get(`open_dependencies`) || defaultOpenDependencies;
};

export const getOpenTasksByProjectId = createSelector(
  getOpenTasks,
  (state, projectId) => projectId,
  (openTasks, projectId) => openTasks.filter((t) => t.project_id === projectId)
);

export const getCompletedTasksByProjectId = createSelector(
  getCompletedTasks,
  (state, projectId) => projectId,
  (completedTasks, projectId) => completedTasks.filter((t) => t.project_id === projectId)
);

const getStatusFilteredTasksByProjectId = createSelector(getAllTasksByProjectId, (tasks) => {
  return tasks.filter((t) => !t.is_completed);
});

/**
 * @deprecated
 */
export const makeAvailablePeopleFiltersByProjectIdSelector = () => {
  const getSelectedAssigneeIdFilter = makeSelectedAssigneeIdFilterSelector();
  return createSelector(getAllTasksByProjectId, getSelectedAssigneeIdFilter, (tasks, selectedAssigneeFilter) => {
    const tasksJS = tasks.toJS();
    const people_filters = {};

    forEach(tasksJS, (task) => {
      const assigneeId =
        task.assignee_id === `null` || task.assignee_id === undefined || task.assignee_id < 1 ? 0 : task.assignee_id;
      const isCompleted = task.is_completed;
      if (!people_filters[assigneeId]) {
        people_filters[assigneeId] = {
          id: assigneeId,
          count: isCompleted ? 0 : 1,
          completed_count: isCompleted ? 1 : 0,
        };
      } else {
        isCompleted ? (people_filters[assigneeId].completed_count += 1) : (people_filters[assigneeId].count += 1);
      }
    });

    if (Array.isArray(selectedAssigneeFilter)) {
      selectedAssigneeFilter.forEach((selectedAssignee) => {
        if (!people_filters[selectedAssignee]) {
          people_filters[selectedAssignee] = {
            id: selectedAssignee,
            count: 0,
            completed_count: 0,
          };
        }
      });
    }

    return Object.values(orderBy(people_filters, `count`, `desc`));
  });
};

export const getAvailableAssigneeIdsFiltersByProjectId = createSelector(
  getAllTasksByProjectId,
  (state, projectId) => getProjectFiltersById(state, projectId),
  (tasks, selectedFilters) => {
    const selectedAssigneeFilter =
      selectedFilters && selectedFilters.has(`peopleFilter`) ? selectedFilters.get(`peopleFilter`)?.toArray() : [];

    const people_filters = {};

    // Iterate over Immutable Map/List of tasks without converting to JS
    tasks.forEach((task) => {
      const isCompleted = !!task.is_completed;
      const ids = List.isList(task.assignee_ids)
        ? task.assignee_ids
        : Array.isArray(task.assignee_ids)
        ? List(task.assignee_ids)
        : List();

      if (ids.size === 0) {
        // Unassigned bucket (0)
        if (!people_filters[0]) {
          people_filters[0] = { id: 0, count: isCompleted ? 0 : 1, completed_count: isCompleted ? 1 : 0 };
        } else {
          isCompleted ? (people_filters[0].completed_count += 1) : (people_filters[0].count += 1);
        }
        return;
      }

      ids.forEach((assigneeId) => {
        const key = Number(assigneeId) > 0 ? Number(assigneeId) : 0;
        if (!people_filters[key]) {
          people_filters[key] = { id: key, count: isCompleted ? 0 : 1, completed_count: isCompleted ? 1 : 0 };
        } else {
          isCompleted ? (people_filters[key].completed_count += 1) : (people_filters[key].count += 1);
        }
      });
    });

    if (Array.isArray(selectedAssigneeFilter)) {
      selectedAssigneeFilter.forEach((selectedAssignee) => {
        const key = Number(selectedAssignee) > 0 ? Number(selectedAssignee) : 0;
        if (!people_filters[key]) {
          people_filters[key] = { id: key, count: 0, completed_count: 0 };
        }
      });
    }

    return Object.values(orderBy(people_filters, `count`, `desc`));
  }
);

export const getPeopleSelectFiltersByProjectId = createSelector(
  getAvailableAssigneeIdsFiltersByProjectId,
  (state) => state.users,
  (availablePeopleFilters, users) => {
    const result = [];
    availablePeopleFilters.forEach((u) => {
      const user = users.get(`${u.id}`);
      result.push({
        id: u.id,
        name: u.id > 0 && user ? `${user.display_name}` : i18n.t(`Unassigned`),
        countOpen: u.count,
        countCompleted: u.completed_count,
        countAll: u.count + u.completed_count,
      });
    });
    return result;
  }
);

/**
 * @deprecated
 */
export const makePeopleSelectFiltersByProjectIdSelector = () => {
  const getAvailablePeopleFilterSelector = makeAvailablePeopleFiltersByProjectIdSelector();
  return createSelector(
    (state, projectId) => getAvailablePeopleFilterSelector(state, projectId),
    (state) => state.users,
    (availablePeopleFilters, users) => {
      const result = [];
      availablePeopleFilters.forEach((u) => {
        const user = users.get(`${u.id}`);
        result.push({
          id: u.id,
          name: user ? `${user.display_name}` : i18n.t(`Unassigned`),
          countOpen: u.count,
          countCompleted: u.completed_count,
          countAll: u.count + u.completed_count,
        });
      });
      return result;
    }
  );
};

export const makeAvailableDateFiltersByProjectIdSelector = () => {
  return createSelector([getAllTasksByProjectId, makeSelectedDateIdFilterSelector()], (tasks, selectedDateFilter) => {
    const date_filters = {
      not_set: {
        id: `not_set`,
        name: i18n.t(`Not Set`),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
      today: {
        id: `today`,
        name: i18n.t(`Today`),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
      overdue: {
        id: `overdue`,
        name: i18n.t(`Overdue`),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
      upcoming: {
        id: `upcoming`,
        name: i18n.t(`Less than 1 week`),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
      scheduled: {
        id: `scheduled`,
        name: i18n.t(`More than 1 week`),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
    };

    tasks.map((task) => {
      switch (task.getDueIndicator()) {
        case `not_set`:
          task.is_completed
            ? (date_filters.not_set.completedTasksCount += 1)
            : (date_filters.not_set.openTasksCount += 1);
          break;
        case `today`:
          task.is_completed ? (date_filters.today.completedTasksCount += 1) : (date_filters.today.openTasksCount += 1);
          break;
        case `overdue`:
          task.is_completed
            ? (date_filters.overdue.completedTasksCount += 1)
            : (date_filters.overdue.openTasksCount += 1);
          break;
        case `upcoming`:
          task.is_completed
            ? (date_filters.upcoming.completedTasksCount += 1)
            : (date_filters.upcoming.openTasksCount += 1);
          break;
        case `scheduled`:
          task.is_completed
            ? (date_filters.scheduled.completedTasksCount += 1)
            : (date_filters.scheduled.openTasksCount += 1);
          break;
      }
    });

    forEach(date_filters, (value, key) => {
      if (value.openTasksCount === 0 && value.completedTasksCount === 0 && !selectedDateFilter?.includes(key)) {
        delete date_filters[key];
      }
    });

    if (Object.keys(date_filters).length === 1) {
      return [];
    }

    return Object.values(orderBy(date_filters, `count`, `desc`));
  });
};

export const makeAvailablePriorityFiltersByProjectIdSelector = () => {
  return createSelector(getAllTasksByProjectId, (tasks) => {
    const normalPriorityTasks = tasks.filter((task) => !task.is_important);
    const highPriorityTasks = tasks.filter((task) => task.is_important);

    if (!normalPriorityTasks.size > 0 || !highPriorityTasks.size > 0) return [];

    const priority_filters = {
      high: {
        id: "high",
        name: i18n.t("High"),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
      normal: {
        id: "normal",
        name: i18n.t("Normal"),
        openTasksCount: 0,
        completedTasksCount: 0,
      },
    };

    tasks.map((task) => {
      if (task.is_important) {
        task.is_completed
          ? (priority_filters.high.completedTasksCount += 1)
          : (priority_filters.high.openTasksCount += 1);
      } else {
        task.is_completed
          ? (priority_filters.normal.completedTasksCount += 1)
          : (priority_filters.normal.openTasksCount += 1);
      }
    });

    return Object.values(priority_filters);
  });
};

export const makeAvailableLabelFiltersByProjectIdSelector = () => {
  const getSelectedLabelIdFilter = makeSelectedLabelIdFilterSelector();
  return createSelector(
    [getAllTasksByProjectId, getAllLabels, getSelectedLabelIdFilter],
    (tasks, labels, selectedLabelFilter) => {
      const labelFilters = {};
      tasks.map((task) => {
        const isCompleted = task.get("is_completed");
        if (!task.labels.size) {
          if (!labelFilters.not_set) {
            labelFilters.not_set = {
              id: -1,
              name: i18n.t(`Not Set`),
              openTasksCount: isCompleted ? 0 : 1,
              completedTasksCount: isCompleted ? 1 : 0,
            };
          } else {
            isCompleted ? (labelFilters.not_set.completedTasksCount += 1) : (labelFilters.not_set.openTasksCount += 1);
          }
        }
        task.labels.map((labelId) => {
          if (labelFilters[labelId]) {
            isCompleted
              ? (labelFilters[labelId].completedTasksCount += 1)
              : (labelFilters[labelId].openTasksCount += 1);
          } else {
            const label = labels.get(`${labelId}`);
            if (label) {
              isCompleted
                ? (labelFilters[labelId] = {
                    id: label.id,
                    name: label.name,
                    completedTasksCount: 1,
                    openTasksCount: 0,
                  })
                : (labelFilters[labelId] = {
                    id: label.id,
                    name: label.name,
                    openTasksCount: 1,
                    completedTasksCount: 0,
                  });
            }
          }
        });
      });

      const labelFiltersArray = [];

      forEach(labelFilters, (value) => {
        labelFiltersArray.push(value);
      });

      selectedLabelFilter?.forEach((selectedLabelFilter) => {
        if (selectedLabelFilter) {
          const selectedExists = labelFiltersArray.some((label) => {
            return label.id === selectedLabelFilter;
          });

          if (!selectedExists) {
            const label = labels.get(`${selectedLabelFilter}`);
            if (selectedLabelFilter === -1 && !labelFiltersArray.some(({ id }) => id === selectedLabelFilter)) {
              labelFiltersArray.push({
                id: -1,
                name: i18n.t(`Not Set`),
                openTasksCount: 0,
                completedTasksCount: 0,
              });
            }
            if (label) {
              labelFiltersArray.push({
                id: label.id,
                name: label.name,
                openTasksCount: 0,
                completedTasksCount: 0,
              });
            }
          }
        }
      });
      if (labelFiltersArray.length === 1) {
        return [];
      }

      return labelFiltersArray.sort((a, b) => descSortCompare(a.openTasksCount, b.openTasksCount));
    }
  );
};

export const getOpenTasksCountByProjectId = createSelector(getStatusFilteredTasksByProjectId, (tasks) => tasks.size);

export const getAllTasksCountByProjectId = createSelector(
  (state, { projectId }) => getAllTasksByProjectId(state, projectId),
  (tasks) => tasks.size
);

const makeGetStatusFilteredTasksByTaskListIdSelector = () =>
  createSelector(
    getAllTasks,
    (state, taskListId) => getMapOfActiveTaskIdsByTaskListId(state)[`${taskListId}`],
    (tasks, taskIds = []) => {
      return taskIds.map((id) => tasks?.get(`${id}`)).filter((task) => !task?.get(`is_trashed`, false));
    }
  );

const filterTasksByFilterId = (tasks, projectFilters, isMultiAssigneeEnabled = true) => {
  return tasks.filter((task) => {
    if (!projectFilters) return true;
    let visible = true;

    if (projectFilters.get(`taskListFilter`)?.size > 0) {
      visible = projectFilters.get(`taskListFilter`).includes(task.task_list_id);
    }
    if (isMultiAssigneeEnabled) {
      if (projectFilters.get(`peopleFilter`)?.size === 0 || !projectFilters.get(`peopleFilter`)) {
        visible = true;
      } else if (
        task.assignee_ids.size === 0 &&
        projectFilters.get(`peopleFilter`)?.size > 0 &&
        projectFilters.get(`peopleFilter`)?.includes(0)
      ) {
        visible = true;
      } else if (projectFilters.get(`peopleFilter`)?.size > 0 && task.assignee_ids.size > 0) {
        visible = task.assignee_ids.some((assigneeId) => projectFilters.get(`peopleFilter`)?.includes(assigneeId));
      } else if (projectFilters.get(`peopleFilter`)) {
        visible = false;
      }
    } else if (!isMultiAssigneeEnabled && projectFilters.get(`peopleFilter`)?.size > 0 && visible) {
      visible = projectFilters.get(`peopleFilter`).includes(task.assignee_id);
    }
    if (projectFilters.get(`dateFilter`)?.size > 0 && visible) {
      if (task.getDueIndicator() === `overdue` && task.get(`is_completed`)) {
        visible = false;
      } else {
        visible = projectFilters.get(`dateFilter`)?.includes(task.getDueIndicator());
      }
    }

    if (projectFilters.get(`labelFilter`)?.size > 0 && visible) {
      // `-1` filter should only show tasks without labels
      if (projectFilters.get(`labelFilter`).includes(-1) && !task.labels.size) {
        visible = true;
      } else {
        let labelVisible = false;
        task.labels.map((labelId) => {
          if (projectFilters.get(`labelFilter`).includes(labelId)) {
            // if any labels match with label filter, then task should be visible
            labelVisible = true;
          }
        });
        visible = labelVisible;
      }
    }

    if (projectFilters.get(`priorityFilter`)?.size > 0 && visible) {
      visible = projectFilters.get(`priorityFilter`).includes(task.is_important ? "high" : "normal");
    }

    return visible;
  });
};

const makeGetFilteredAllTasksByTaskListIdSelector = () =>
  createSelector(
    makeGetStatusFilteredTasksByTaskListIdSelector(),
    makeGetProjectFiltersByTaskListIdSelector(),
    getIsMultipleAssigneesEnabled,
    (tasks, projectFilters, isMultiAssigneeEnabled) =>
      filterTasksByFilterId(tasks, projectFilters, isMultiAssigneeEnabled)
  );

export const makeTaskNameByIdSelector = () => {
  return createSelector(
    (...attrs) => getTaskById(...attrs)?.name,
    (...attrs) => getTaskById(...attrs)?.task_number,
    (state) => getConfigOption(state, `show_task_id`),
    (state, id, forceWithoutNumber) => forceWithoutNumber,
    (taskName, taskNumber, showTaskId, forceWithoutNumber = false) => {
      if (showTaskId && taskNumber && !forceWithoutNumber) {
        taskName = `#${taskNumber}: ${taskName}`;
      }
      return taskName;
    }
  );
};

export const makeFilteredOpenTaskIdsByTaskListSelector = () => {
  return createSelector(makeGetFilteredAllTasksByTaskListIdSelector(), (tasks) => {
    return tasks
      .filter((t) => !t.get("is_completed"))
      .sort((a, b) => sortCompare(a.get("position"), b.get("position")))
      .map((t) => t.get(`id`));
  });
};

export const makeFilteredCompletedTaskIdsByTaskListSelector = () => {
  return createSelector(makeGetFilteredAllTasksByTaskListIdSelector(), (tasks) =>
    tasks
      .filter((t) => t.get("is_completed"))
      .sort(
        (a, b) =>
          descSortCompare(a.get("completed_on"), b.get("completed_on")) || descSortCompare(a.get("id"), b.get("id"))
      )
      .map((t) => t.get(`id`))
  );
};

const makeGetTaskLabelIdsListSelector = () => createSelector(getTaskById, (task = Map()) => task.get(`labels`, List()));

export const makeTaskLabelIdsSelector = () => {
  return createSelector(makeGetTaskLabelIdsListSelector(), (taskLabelIds) => taskLabelIds.toArray());
};

const makeGetTaskDueIndicatorSelector = () =>
  createSelector(
    (state, id) => getTaskById(state, id)?.due_on,
    (timestamp) => {
      if (!timestamp) return `not_set`;

      let date = moment.utc(timestamp * 1000).startOf(`day`);
      let now = moment().startOf(`day`);

      // today
      if (date.format(`YYYY-MM-DD`) === now.format(`YYYY-MM-DD`)) {
        return `today`;
      }

      // overdue
      if (!date.isAfter(now)) {
        return `overdue`;
      }

      // upcoming
      if (moment(date).isBefore(now.add(7, `days`))) {
        return `upcoming`;
      }

      // schedule in future
      return `scheduled`;
    }
  );

export const makeIsTaskOverdueSelector = () => {
  return createSelector(makeGetTaskDueIndicatorSelector(), (overdue) => overdue === `overdue`);
};

export const makeIsTaskDueTodaySelector = () => {
  return createSelector(makeGetTaskDueIndicatorSelector(), (overdue) => overdue === `today`);
};

export const makeIsTaskUpcomingSelector = () => {
  return createSelector(makeGetTaskDueIndicatorSelector(), (overdue) => overdue === `upcoming`);
};

export const makeIsTaskScheduledSelector = () => {
  return createSelector(makeGetTaskDueIndicatorSelector(), (overdue) => overdue === `scheduled`);
};

const prepareTaskViewUrl = (url) => {
  if (url && url[0] === `/`) {
    return url.substring(1);
  }
  return url;
};

export const getTaskViewUrl = createSelector(getTaskById, (task) => {
  if (task && task.url_path) {
    return prepareTaskViewUrl(task.url_path);
  }
  return "";
});

export const getTaskHashUrl = createSelector(getTaskById, (task) => task && `Task-${task.id}-${task.project_id}`);

export const makeIsTaskFromRecurringSelector = () => {
  return createSelector(
    (state, id) => getDefaultTaskById(state, id).created_from_recurring_task_id,
    (value) => !!value && value > 0
  );
};

export const makeGetTaskListIdByTaskIdSelector = () => {
  return createSelector(getTaskById, (task) => task.task_list_id);
};

export const makeGetOpenSubtaskCountByIdSelector = () => {
  return createSelector(getTaskById, (task) => task.open_subtasks);
};

export const makeGetCommentsCountByIdSelector = () => {
  return createSelector(getTaskById, (task) => task.comments_count);
};

export const makeGetIsTaskCompletedByIdSelector = () => {
  return createSelector(getTaskById, (task) => !!task?.get(`is_completed`, false));
};

export const makeGetProjectIdByTaskId = () => {
  return createSelector(getTaskById, (task) => task.project_id);
};

export const makeGetOpenDependenciesCountByIdSelector = () => {
  return createSelector(getOpenDependenciesById, (openDependencies) => {
    return openDependencies.get(`parents_count`, 0) + openDependencies.get(`children_count`, 0);
  });
};

export const makeGetOpenParentDependenciesCountByIdSelector = () => {
  return createSelector(getOpenDependenciesById, (openDependencies) => {
    return openDependencies.get(`parents_count`, 0);
  });
};

export const getFilteredTasks = createSelector(
  getOpenTasksByProjectId,
  getProjectFiltersById,
  getIsMultipleAssigneesEnabled,
  (tasks, projectFilters, isMultiAssignee) => filterTasksByFilterId(tasks, projectFilters, isMultiAssignee)
);

export const getAllProjectTasksFiltered = createSelector(
  getAllTasksByProjectId,
  getProjectFiltersById,
  getIsMultipleAssigneesEnabled,
  (tasks, projectFilters, isMultiAssignee) => filterTasksByFilterId(tasks, projectFilters, isMultiAssignee)
);

const taskHasDueDateOrSubtasksWithDueDate = (task, subtasks) => {
  if (task.get(`due_on`) > 0 && task.get(`start_on`) > 0) {
    return true;
  }
  if (task.get(`open_subtasks`) === 0 || task.get(`is_completed`)) {
    return false;
  }
  return Boolean(
    subtasks.filter((subtask) => subtask?.due_on && subtask.task_id === task.get(`id`) && !subtask.is_completed).length
  );
};

export const getTasksForGantt = createSelector(
  getOpenTasks,
  (state) => state.subtasks,
  (tasks, subtasks) => tasks.filter((task) => taskHasDueDateOrSubtasksWithDueDate(task, Object.values(subtasks)))
);

const countTasks = (taskIds, allTasks) => {
  return taskIds.reduce(
    (prev, next) => {
      const task = allTasks.get(`${next}`);
      if (task) {
        const isOpen = !task.get("is_completed");
        prev = {
          ...prev,
          open: isOpen ? prev.open + 1 : prev.open,
          completed: !isOpen ? prev.completed + 1 : prev.completed,
        };
      }
      return prev;
    },
    {
      open: 0,
      completed: 0,
      total: taskIds.length,
    }
  );
};

/**
 * Return counts by task_list_id collection
 */
export const getTaskCountersCollectionSelector = createSelector(
  getMapOfActiveTaskIdsByTaskListId,
  getAllTasks,
  (collection, tasks) => {
    return Object.keys(collection).reduce((prev, next) => {
      const ids = collection[next] || [];
      prev[next] = countTasks(ids, tasks);
      return prev;
    }, {});
  }
);

const makeGetTasksByProjectSelector = (projectId) => {
  return createSelector(getOpenAndCompletedTasks, (tasks) => {
    return tasks.filter((task) => {
      return !task.get(`is_trashed`) && task.get(`project_id`) === projectId;
    });
  });
};

const makeGetFilteredTasksByProjectSelector = (projectId) => {
  const getTasksByProject = makeGetTasksByProjectSelector(projectId);
  return createSelector(
    getTasksByProject,
    (state) => getProjectFiltersById(state, projectId),
    getIsMultipleAssigneesEnabled,
    (tasks, projectFilters, isMultiAssignee) => filterTasksByFilterId(tasks, projectFilters, isMultiAssignee)
  );
};

export const makeGetFilteredCompletedTaskIdsByProjectSelector = (projectId) => {
  const getFilteredTasksByProject = makeGetFilteredTasksByProjectSelector(projectId);
  return createSelector(getFilteredTasksByProject, (tasks) => {
    return tasks
      .filter((task) => task.get("is_completed"))
      .sort((a, b) => {
        return (
          descSortCompare(a.get("completed_on"), b.get("completed_on")) || descSortCompare(a.get("id"), b.get("id"))
        );
      })
      .map((task) => task.get("id"))
      .toArray();
  });
};

export const makeGetTaskByIdForColumnViewSelector = (taskId) => {
  return createSelector(
    (state) => getTaskById(state, taskId),
    (task) => {
      if (!task) {
        return undefined;
      }
      const open_dependencies = task.get("open_dependencies") || Map();
      return {
        id: task.get("id"),
        task_list_id: task.get("task_list_id"),
        name: task.get("name"),
        assignee_id: task.get("assignee_id"),
        assignee_ids: task.get("assignee_ids"),
        open_subtasks: task.get("open_subtasks"),
        comments_count: task.get("comments_count"),
        is_completed: task.get("is_completed"),
        completed_on: task.get("completed_on"),
        is_important: task.get("is_important"),
        position: task.get("position"),
        is_hidden_from_clients: task.get("is_hidden_from_clients"),
        start_on: task.get("start_on"),
        due_on: task.get("due_on"),
        open_dependencies: {
          children_count: open_dependencies.get("children_count") || 0,
          parents_count: open_dependencies.get("parents_count") || 0,
        },
        labels: task.get("labels", List()).toArray(),
        hashUrl: `Task-${task.get("id")}-${task.get("project_id")}`,
        viewUrl: prepareTaskViewUrl(task.get("url_path")),
        task_number: task.get("task_number"),
      };
    }
  );
};

export const makeGetTaskLabelIdsSelector = (id) => {
  const getTaskLabelIdsList = makeGetTaskLabelIdsListSelector();
  return createSelector(
    (state) => getTaskLabelIdsList(state, id),
    (labels) => labels.toArray()
  );
};

export const getAssigneeId = createSelector(getTaskById, (task) => task.assignee_id);

export const makeGetTasksArchiveByProjectId = () => {
  return createSelector(
    getCompletedTasksByProjectId,
    getCompletedTaskListsByProjectId,
    (completedProjectTasks, completedProjectTaskLists) => {
      const result = [];

      completedProjectTasks?.valueSeq().forEach((task) => {
        result.push(task.toJS());
      });

      completedProjectTaskLists?.valueSeq().forEach((taskList) => {
        result.push(taskList.toJS());
      });

      result.sort((first, second) => {
        if (first.completed_on > second.completed_on) {
          return -1;
        } else if (first.completed_on < second.completed_on) {
          return 1;
        }

        if (first.class === "TaskList") {
          return -1;
        } else {
          return 1;
        }
      });

      return result;
    }
  );
};

export const makeGetFilteredTasksArchiveByProjectId = () => {
  const getTasksArchiveByProjectId = makeGetTasksArchiveByProjectId();
  return createSelector(
    getTasksArchiveByProjectId,
    (state, projectId, filters) => filters,
    (state, projectId, filters, boundaryCursor) => boundaryCursor,
    (tasksArchive, filters, boundaryCursor) => {
      const results = tasksArchive.filter((item) => {
        const itemIsTaskList = item.class === "TaskList";
        if (filters?.taskListFilter?.length) {
          const taskListId = itemIsTaskList ? item.id : item.task_list_id;
          if (filters?.taskListFilter.indexOf(taskListId) < 0) return false;
        }
        if (filters?.assigneeFilter?.length) {
          if (itemIsTaskList) return false;

          const assigneeId = item.assignee_id || 0;
          if (filters?.assigneeFilter?.indexOf(assigneeId) < 0) return false;
        }
        if (filters?.dueDateFilter?.length) {
          if (itemIsTaskList) return false;

          if (filters?.dueDateFilter === "not_set") {
            if (item.due_on) return false;
          } else if (!filterByDate(item.due_on, filters?.dueDateFilter)) {
            return false;
          }
        }
        if (filters?.labelFilter?.length) {
          if (itemIsTaskList) return false;
          const itemLabelsSet = new Set(item.labels.length ? item.labels : [0]);

          if (filters?.labelFilter.filter(Set.prototype.has, itemLabelsSet).length < 1) return false;
        }
        if (filters?.completionDateFilter?.length) {
          if (!filterByDate(item.completed_on, filters?.completionDateFilter)) return false;
        }
        if (filters?.completedByFilter?.length) {
          const completedById = item.completed_by_id || 0;
          if (filters?.completedByFilter?.indexOf(completedById) < 0) return false;
        }
        return true;
      });

      // perform pagination in case we need to filter out object that sneaked
      // into redux stores and fit previous filter criteria
      // object could have sneaked in by using actions other than pagination
      // action or by being loaded in previous page renderings
      if (boundaryCursor) {
        let boundaryElementIndex = results.findIndex((element) => {
          if (element?.class && element?.id) {
            let element_cursor_class = element.class === "Task" ? "task" : "task-list";
            let element_cursor = element_cursor_class + "-" + element.id;
            if (element_cursor === boundaryCursor) {
              return true;
            }
          }
        });

        if (boundaryElementIndex !== -1) {
          if (results.length > boundaryElementIndex + 1) {
            results.length = boundaryElementIndex + 1;
          }
        }
      }

      return results;
    }
  );
};

export const getTasksByDate = createSelector(
  getOpenTasks,
  getFilterCalendarConfigOption,
  (state, date) => moment.utc(date).startOf("day"),
  (state) => state.logged_user,
  (tasks, filters, date, loggedUser) => {
    let filterMembers = filters?.user || [];
    let filterProject = filters?.project || [];

    if (filterMembers.length === 0) {
      filterMembers = undefined;
    }
    if (filterProject.length === 0) {
      filterProject = undefined;
    }

    const result = Object.values(tasks.toJS()).filter((task) => {
      const isTaskVisible =
        loggedUser &&
        (loggedUser.class !== "Client" || (loggedUser.class === "Client" && !task.is_hidden_from_clients));

      const isDateInRange =
        task.start_on &&
        task.due_on &&
        moment.unix(task.start_on).startOf("day").isSameOrBefore(date, "day") &&
        moment.unix(task.due_on).startOf("day").isSameOrAfter(date, "day");

      if (!isTaskVisible) {
        return false;
      }

      if (filterMembers && filterProject) {
        return isDateInRange && filterMembers.includes(task.assignee_id) && filterProject.includes(task.project_id);
      } else if (!filterMembers && filterProject) {
        return isDateInRange && filterProject.includes(task.project_id);
      } else if (filterMembers && !filterProject) {
        return isDateInRange && filterMembers.includes(task.assignee_id);
      } else {
        return isDateInRange;
      }
    });

    return result;
  }
);

export const getTrashedTaskIds = createSelector(
  getAllTasks,
  (state, projectId) => projectId,
  (tasks, projectId) => {
    return tasks
      .filter((t) => t.is_trashed && t.project_id === projectId)
      .map((t) => t.id)
      .toArray();
  }
);
